/**
 * Browser-side resize + re-encode for website image uploads (#735).
 *
 * Staff upload phone photos and screenshots that are often 2–20 MB. These run before the
 * file reaches Storage, so a new upload is stored at a web size. Display doesn't depend on
 * it: `supabaseImageUrl` in `lib/seo.ts` serves a resized WebP of whatever is stored, so
 * any failure here falls back to uploading the original rather than blocking the upload.
 */

/** Longest edge a stored website image keeps. Covers render at 1200w, dish photos at 960w. */
export const MAX_UPLOAD_EDGE = 2560;

/** Encoder quality (0–1) for both WebP and the JPEG fallback. */
const UPLOAD_QUALITY = 0.82;

/**
 * Raster types worth re-encoding. GIF is left alone because a canvas keeps only the first
 * frame; SVG, AVIF and HEIC because re-encoding gains nothing or the browser can't decode it.
 */
const COMPRESSIBLE = new Set(["image/jpeg", "image/png", "image/webp"]);

const EXTENSIONS: Record<string, string> = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
};

/** Scale `width` x `height` down so the longest edge is at most `max`. Never scales up. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** `name` with its extension replaced by the one for `type` (or appended if it had none). */
export function withExtension(name: string, type: string): string {
  const ext = extensionFor(type);
  if (!ext) return name;
  const dot = name.lastIndexOf(".");
  const slash = name.lastIndexOf("/");
  const stem = dot > slash ? name.slice(0, dot) : name;
  return `${stem}.${ext}`;
}

/** The file extension for an image MIME type, or `null` for one this module doesn't produce. */
export function extensionFor(type: string): string | null {
  return EXTENSIONS[type] ?? null;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d");
  if (!ctx) return true;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

/**
 * Encode a canvas as WebP. Safari's canvas can't encode WebP and quietly hands back a PNG
 * instead; then this encodes JPEG when the image is opaque and keeps the PNG when it isn't.
 * Pass `opaque` when the source can't have transparency (a JPEG) to skip the pixel scan.
 * The returned blob's `type` is what was actually produced.
 */
export async function encodeCanvas(canvas: HTMLCanvasElement, opaque = false): Promise<Blob | null> {
  const webp = await toBlob(canvas, "image/webp", UPLOAD_QUALITY);
  if (!webp || webp.type === "image/webp") return webp;
  if (!opaque && hasTransparency(canvas)) return webp;
  return (await toBlob(canvas, "image/jpeg", UPLOAD_QUALITY)) ?? webp;
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (err) {
    // An engine that doesn't know the option throws a TypeError; current browsers apply
    // EXIF orientation by default, so decode without it. A real decode failure rethrows.
    if (err instanceof TypeError) return createImageBitmap(file);
    throw err;
  }
}

/**
 * Resize (long edge ≤ `MAX_UPLOAD_EDGE`) and re-encode a JPEG/PNG/WebP upload.
 *
 * Returns the re-encoded file, renamed to match its new type, only when it is smaller than
 * the input. Anything else (another type, a decode or encode failure, a bigger result)
 * returns the input unchanged.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  if (!COMPRESSIBLE.has(file.type)) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await decode(file);
  } catch {
    return file; // undecodable here (corrupt, or too large to allocate): upload as-is
  }

  let canvas: HTMLCanvasElement | undefined;
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, MAX_UPLOAD_EDGE);
    canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await encodeCanvas(canvas, file.type === "image/jpeg");
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], withExtension(file.name, blob.type), {
      type: blob.type,
      lastModified: Date.now(),
    });
  } catch {
    return file; // canvas allocation or encode failed: upload as-is
  } finally {
    bitmap.close();
    // iOS Safari caps total canvas memory; free this one rather than wait for GC.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
